/**
 * Members — the roster, with filters, a bulk bar and the profile drawer.
 *
 * Status and balance are DERIVED, not stored, so this screen needs the
 * requests as well as the people: `/people` alone cannot say whether someone
 * is overdue. Both are fetched together and handed to NetlyPayManager, which
 * is the same arithmetic the API uses.
 *
 * Before the shop is linked this renders the Enable screen, because
 * `not_linked` is a state and not an error (D54).
 *
 * Spec: DESIGN-BRIEF.md §C and `vMembers()` in prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {MagnifyingGlassIcon, PlusIcon} from '@heroicons/react/24/outline';
import NetlyPayRepository from '../../../internals/repository/NetlyPayRepository';
import type {Member, NetlyPayData} from '../../../internals/managers/NetlyPayManager';
import {memberTable, selectionSummary, NO_FILTERS, STATUS_FILTERS} from '../members';
import {useNetlyPayFetch, useNetlyPayLink} from '../hooks/useNetlyPay';
import {EmptyState, ErrorState, Skeleton} from '../components/States';
import {MemberStatusChip} from '../components/StatusChip';
import {GhostButton, PrimaryButton} from '../components/Overlays';
import EnableNetlyPayView from './EnableNetlyPayView';
import MemberDrawer from './MemberDrawer';
import MemberFormModal from './MemberFormModal';

const selectCls = 'nl-select w-auto';

const th = 'px-4 py-2.5 text-left text-[12px] font-semibold text-text3 whitespace-nowrap';
const td = 'px-4 py-3 text-[13px] text-text1 align-middle';

export const MembersView = ({selectedShop}: any) => {
  const shopId = selectedShop?.shopId;
  const currency = selectedShop?.currency ?? 'Rs.';
  const link = useNetlyPayLink(shopId);

  const [filters, setFilters] = useState(NO_FILTERS);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [form, setForm] = useState<{member: Member | null} | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  /**
   * People and requests together. The alternative — asking the API for a
   * pre-computed status — would put the same rule in two places and let the
   * table disagree with the drawer.
   */
  const roster = useNetlyPayFetch<NetlyPayData>(
    link.phase === 'linked' ? shopId : null,
    async (id) => {
      const [people, requests] = await Promise.all([
        NetlyPayRepository.listMembers(id),
        NetlyPayRepository.listRequests(id),
      ]);
      return {
        members: people?.members ?? [],
        programs: people?.programs ?? [],
        batches: people?.batches ?? [],
        requests: requests?.requests ?? [],
      };
    },
    [link.phase],
    (d) => (d?.members?.length ?? 0) === 0,
  );

  const data = roster.data ?? {members: [], programs: [], batches: [], requests: []};
  const table = useMemo(
    () => memberTable(data, filters, undefined, currency),
    [data, filters, currency],
  );

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allShown = table.rows.length > 0 && table.rows.every((r) => selected[r.id]);
  const openMember = openId ? data.members.find((m) => m.id === openId) ?? null : null;

  const flash = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  };

  const batchOptions = useMemo(
    () =>
      data.batches.filter(
        (b) => filters.programId === 'all' || b.programId === filters.programId,
      ),
    [data.batches, filters.programId],
  );

  const saveMember = async (draft: any) => {
    if (!shopId) return;
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        phone: draft.phone,
        email: draft.email || undefined,
        dob: draft.dob || undefined,
        joined: draft.joined,
        batchId: draft.batchId,
        guardians: draft.guardians,
        custom: draft.custom,
      };
      if (form?.member) {
        await NetlyPayRepository.updateMember(shopId, form.member.id, body);
        flash('Member saved');
      } else {
        const res = await NetlyPayRepository.createMember(shopId, body);
        flash(`${draft.name} added${res?.member?.id ? ` as ${res.member.id}` : ''}`);
      }
      setForm(null);
      roster.reload();
    } catch (e: any) {
      flash(e?.message ?? 'Could not save the member');
    } finally {
      setSaving(false);
    }
  };

  const remind = async (ids: string[]) => {
    if (!shopId || !ids.length) return;
    try {
      await NetlyPayRepository.remind(shopId, {memberIds: ids});
      flash(`Reminder sent for ${ids.length} ${ids.length === 1 ? 'member' : 'members'}`);
      setSelected({});
      roster.reload();
    } catch (e: any) {
      flash(e?.message ?? 'Could not send the reminders');
    }
  };

  const linkCustomer = async (customerId: string) => {
    if (!shopId || !openId) return;
    try {
      await NetlyPayRepository.updateMember(shopId, openId, {posCustomerId: customerId} as any);
      flash('Linked to the POS customer');
      roster.reload();
    } catch (e: any) {
      flash(e?.message ?? 'Could not link the customer');
    }
  };

  // ── Screen states before the table ────────────────────────────────────────

  if (link.phase === 'loading') {
    return (
      <div className="p-7">
        <div className="bg-surface border border-borderc rounded-card">
          <Skeleton rows={5} />
        </div>
      </div>
    );
  }

  if (link.phase === 'error') {
    return (
      <div className="p-7">
        <div className="bg-surface border border-borderc rounded-card">
          <ErrorState message={link.error} onRetry={link.reload} />
        </div>
      </div>
    );
  }

  if (link.phase === 'not_linked') {
    return (
      <div className="p-7">
        <EnableNetlyPayView shopId={shopId} shop={selectedShop} onEnabled={link.reload} />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full p-7 gap-5 nl-rise">
      <div className="flex items-end justify-between gap-5 flex-wrap">
        <div>
          <h1 className="text-[26px] font-semibold text-text1 tracking-[-0.01em] leading-tight">
            Members
          </h1>
          <p className="text-[13px] text-text2 mt-1">
            {roster.phase === 'ready' || roster.phase === 'empty' ? table.summary : 'Loading…'}
          </p>
        </div>
        <PrimaryButton onClick={() => setForm({member: null})}>
          <span className="flex items-center gap-1.5">
            <PlusIcon className="w-4 h-4" />
            Add member
          </span>
        </PrimaryButton>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-text3 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={filters.q}
            onChange={(e) => setFilters({...filters, q: e.target.value})}
            placeholder="Search name, number or phone"
            aria-label="Search members"
            className={`${selectCls} pl-9 w-[290px]`}
          />
        </div>
        <select
          value={filters.programId}
          onChange={(e) => setFilters({...filters, programId: e.target.value, batchId: 'all'})}
          aria-label="Filter by program"
          className={selectCls}
        >
          <option value="all">All programs</option>
          {data.programs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          value={filters.batchId}
          onChange={(e) => setFilters({...filters, batchId: e.target.value})}
          aria-label="Filter by batch"
          className={selectCls}
        >
          <option value="all">All batches</option>
          {batchOptions.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => {
          const on = filters.status === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setFilters({...filters, status: s.key})}
              className={`h-8 px-3 rounded-full text-[12.5px] font-medium border transition-colors ${
                on
                  ? 'bg-brand-soft border-brand text-brand'
                  : 'bg-surface border-borderc text-text2 hover:bg-surface2'
              }`}
            >
              {s.label}
              <span className="ml-1.5 font-mono tabular-nums text-text3">
                {table.counts[s.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-surface border border-borderc rounded-card overflow-hidden">
        {selectedIds.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-brand-soft/50 border-b border-borderc flex-wrap">
            <p className="text-[12.5px] font-medium text-text1">
              {selectionSummary(data, selectedIds, currency)}
            </p>
            <span className="flex items-center gap-2.5">
              <GhostButton onClick={() => remind(selectedIds)}>Remind</GhostButton>
              <GhostButton onClick={() => flash('Export is not wired up yet')}>Export</GhostButton>
              <button
                type="button"
                onClick={() => setSelected({})}
                className="text-[12.5px] text-text3 hover:text-text1"
              >
                Clear
              </button>
            </span>
          </div>
        )}

        {roster.phase === 'loading' && <Skeleton rows={6} />}

        {roster.phase === 'error' && <ErrorState message={roster.error} onRetry={roster.reload} />}

        {roster.phase === 'empty' && (
          <EmptyState
            title="No members yet"
            text="Add the first member, or bring your list in from a spreadsheet."
            action={<PrimaryButton onClick={() => setForm({member: null})}>Add member</PrimaryButton>}
          />
        )}

        {roster.phase === 'ready' && table.rows.length === 0 && (
          <EmptyState
            title="No members match"
            text="Nothing matches the search and filters above."
            action={<GhostButton onClick={() => setFilters(NO_FILTERS)}>Clear filters</GhostButton>}
          />
        )}

        {roster.phase === 'ready' && table.rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-surface2">
                <tr>
                  <th className={`${th} w-10`}>
                    <input
                      type="checkbox"
                      checked={allShown}
                      onChange={() => {
                        if (allShown) {
                          setSelected({});
                          return;
                        }
                        const next: Record<string, boolean> = {};
                        table.rows.forEach((r) => {
                          next[r.id] = true;
                        });
                        setSelected(next);
                      }}
                      aria-label="Select all shown"
                      className="w-4 h-4 accent-[var(--brand)] cursor-pointer"
                    />
                  </th>
                  <th className={th}>MEMBER NO.</th>
                  <th className={th}>NAME</th>
                  <th className={th}>PHONE</th>
                  <th className={th}>PROGRAM · BATCH</th>
                  <th className={th}>STATUS</th>
                  <th className={`${th} text-right`}>BALANCE</th>
                  <th className={`${th} text-right`}>LAST PAYMENT</th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setOpenId(row.id)}
                    className={`border-t border-borderc cursor-pointer transition-colors ${
                      selected[row.id] ? 'bg-brand-soft/30' : 'hover:bg-surface2'
                    }`}
                  >
                    <td className={td} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!!selected[row.id]}
                        onChange={() => setSelected({...selected, [row.id]: !selected[row.id]})}
                        aria-label={`Select ${row.name}`}
                        className="w-4 h-4 accent-[var(--brand)] cursor-pointer"
                      />
                    </td>
                    <td className={`${td} font-mono text-text2 whitespace-nowrap`}>{row.id}</td>
                    <td className={td}>
                      <span className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-full bg-surface3 text-text2 text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                          {row.initials}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-medium text-text1 truncate">{row.name}</span>
                          <span className="block text-[11.5px] text-text3 truncate">
                            {row.guardian}
                          </span>
                        </span>
                      </span>
                    </td>
                    <td className={`${td} font-mono text-text2 whitespace-nowrap`}>{row.phone}</td>
                    <td className={`${td} whitespace-nowrap`}>{row.group}</td>
                    <td className={td}>
                      <MemberStatusChip status={row.status} />
                    </td>
                    <td
                      className={`${td} font-mono tabular-nums text-right whitespace-nowrap ${
                        row.balanceTone === 'danger'
                          ? 'text-neg'
                          : row.balanceTone === 'muted'
                          ? 'text-text3'
                          : 'text-text1'
                      }`}
                    >
                      {row.balance}
                    </td>
                    <td className={`${td} font-mono tabular-nums text-right text-text2 whitespace-nowrap`}>
                      {row.lastPayment}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openMember && (
        <MemberDrawer
          data={data}
          member={openMember}
          currency={currency}
          onClose={() => setOpenId(null)}
          onEdit={() => {
            setForm({member: openMember});
            setOpenId(null);
          }}
          onRemind={() => remind([openMember.id])}
          onLinkCustomer={linkCustomer}
        />
      )}

      {form && (
        <MemberFormModal
          data={data}
          batches={data.batches}
          member={form.member}
          saving={saving}
          onCancel={() => setForm(null)}
          onSave={saveMember}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-xl bg-text1 text-surface text-[13px] font-medium"
          style={{boxShadow: '0 12px 32px rgba(15,15,26,.24)'}}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default connect((state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
}))(MembersView);
